import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Bell, BellOff, TestTube2, X } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
  registerServiceWorker,
} from "../lib/push";

const DISMISS_KEY = "fastvi.push.banner.dismissed";

export function PushBanner() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && window.localStorage?.getItem(DISMISS_KEY) === "1"
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    setSupported(true);
    setPermission(Notification.permission);
    // Register the SW eagerly so `push` events can arrive even without a
    // subscription (safe idempotent no-op if already registered).
    registerServiceWorker().catch(() => {});
    (async () => {
      if (!("serviceWorker" in navigator)) return;
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      setSubscribed(Boolean(sub));
    })();
  }, []);

  async function enable() {
    setBusy(true);
    try {
      const res = await subscribeToPush();
      setPermission(res.permission);
      if (res.permission === "granted") {
        setSubscribed(true);
        toast.success("Doorbell notifications enabled");
      } else {
        toast("Permission dismissed — you can enable it later in browser settings.");
      }
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast("Doorbell notifications turned off");
    } catch (e) {
      toast.error(e.message || "Could not disable notifications");
    } finally {
      setBusy(false);
    }
  }

  async function testPush() {
    setBusy(true);
    try {
      const data = await sendTestPush();
      toast.success(`Test sent · delivered:${data.sent} · cleaned:${data.cleaned}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test push failed");
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    setDismissed(true);
    try { window.localStorage?.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  }

  if (!supported) {
    return (
      <div className="card-stroke p-4 flex items-center gap-3 mb-6 fade-up" data-testid="push-banner-unsupported">
        <BellOff size={16} className="text-muted-foreground shrink-0" />
        <div className="text-sm text-muted-foreground">
          This browser does not support Web Push. To receive doorbell rings, install FastVi
          as a PWA on iOS (16.4+) or use Chrome / Firefox / Edge on desktop / Android.
        </div>
      </div>
    );
  }

  if (permission === "denied") {
    return (
      <div className="card-stroke p-4 flex items-center gap-3 mb-6 fade-up" data-testid="push-banner-denied">
        <BellOff size={16} className="text-muted-foreground shrink-0" />
        <div className="text-sm text-muted-foreground">
          Doorbell notifications are blocked in this browser. Open the site settings
          (lock icon in the URL bar) to allow notifications, then reload.
        </div>
      </div>
    );
  }

  if (subscribed) {
    return (
      <div className="card-stroke p-4 flex items-center gap-3 mb-6 fade-up" data-testid="push-banner-enabled">
        <Bell size={16} className="text-accent shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Doorbell notifications: ON</div>
          <div className="text-xs text-muted-foreground">
            You'll get a system notification when a visitor rings your unit — even if this tab is closed.
          </div>
        </div>
        <Button
          size="sm" variant="outline" onClick={testPush} disabled={busy}
          data-testid="push-test-button"
        >
          <TestTube2 size={14} className="mr-1.5" /> Test
        </Button>
        <Button
          size="sm" variant="ghost" onClick={disable} disabled={busy}
          data-testid="push-disable-button"
        >
          <BellOff size={14} className="mr-1.5" /> Turn off
        </Button>
      </div>
    );
  }

  if (dismissed) return null;

  return (
    <div className="card-stroke p-4 flex items-center gap-3 mb-6 fade-up" data-testid="push-banner">
      <Bell size={16} className="text-accent shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">Never miss the doorbell</div>
        <div className="text-xs text-muted-foreground">
          Get a notification when a visitor rings your unit, even with the tab closed.
        </div>
      </div>
      <Button size="sm" onClick={enable} disabled={busy} data-testid="push-enable-button">
        <Bell size={14} className="mr-1.5" /> Enable
      </Button>
      <Button size="icon" variant="ghost" onClick={dismiss} aria-label="Dismiss" data-testid="push-dismiss">
        <X size={14} />
      </Button>
    </div>
  );
}
